import { useMemo } from 'react';
import { usePlatform } from './usePlatform';

/**
 * Backward-compatible hook over usePlatform.
 *
 * For new code, prefer usePlatform() directly. This shim exists so existing
 * pages that destructure { tg, BackButton, MainButton, ... } continue to
 * compile and work on both Telegram and MAX without changes.
 *
 * On MAX:
 *  - tg returns the MAX WebApp object (different surface than Telegram WebApp,
 *    but pages typically only access fields via optional chaining like
 *    `tg?.BackButton?.show()` or `tg?.HapticFeedback?.notificationOccurred(...)`,
 *    which works because MAX provides BackButton/HapticFeedback with the
 *    same shape).
 *  - BackButton resolves to the MAX BackButton (same API).
 *  - MainButton is null (MAX has no MainButton; not currently used in this app).
 */
export const useTelegram = () => {
  const platform = usePlatform();
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;
  const max = typeof window !== 'undefined' ? window.WebApp : undefined;

  return useMemo(() => {
    // Type strategy: the legacy `tg` is typed as the Telegram WebApp shape so
    // existing callers (e.g. `tg?.showConfirm('...', (confirmed) => ...)`)
    // keep their parameter types. On MAX, we cast the MAX object into the
    // same type slot - methods that don't exist on MAX (showAlert, showConfirm,
    // expand, etc.) are accessed via optional chaining in callers and resolve
    // to `undefined` at runtime, which is safely a no-op.
    const rawTg: typeof tg =
      platform.platform === 'max'
        ? (max as unknown as typeof tg)
        : tg;

    return {
      tg: rawTg,
      user: platform.user,
      initData: platform.initData,
      themeParams: tg?.themeParams,
      // MainButton is Telegram-only. MAX does not expose it.
      MainButton: tg?.MainButton ?? null,
      // BackButton API is identical on both platforms - return whichever exists.
      BackButton:
        platform.platform === 'max' ? max?.BackButton : tg?.BackButton,
    };
  }, [platform, tg, max]);
};
